/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { validate } from 'app/shared/elements/ess-rule';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { ComponentBase, IForm, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { IPopup } from 'app/shared/elements/popup-base';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { ProviderService } from 'app/cd/services/provider-service';
import { MemberService } from 'app/cd/services/member-service';
import { deepClone } from 'app/shared/lib/utilities';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { IDialogOptions, IModalData, openGlobalModal } from 'app/shared/elements/ess-global-modal';
import { ICustomFieldTargetColumnsContainer } from 'app/shared/lib/custom-field-definitions'
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import * as CD from 'app/cd/models/essette-api';

@autoinject()
export class MemberProvidersGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public caseId: string | null = null;

    protected providers: ReadonlyArray<CD.Provider> = [];

    @bindable protected readonly: boolean = true;
    @bindable protected configurable: boolean = true;

    protected showProviderSummaryLink: boolean = false;
    protected openProviderSummary = openProviderSummary;

    protected editedMemberProvider: CD.MemberProvider = {};
    protected validateForm = validate;
    protected saveMemberProviderFormIsValid: boolean = false;

    protected memberPlans: ReadonlyArray<CD.MemberPlan> | null = null;
    protected relationships: ReadonlyArray<CD.MemberProviderRelationship> | null = null;

    protected providerCustomFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;
    protected providerFacilityCustomFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;
    protected providerGroupCustomFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    ////****************** Element References ***************************************////
    private $saveMemberProviderModal!: IPopup;
    protected $saveMemberProviderForm!: IForm;
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly providerService: ProviderService,
        private readonly memberService: MemberService,
        private readonly customFieldContext: CustomFieldContext,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
        this.sorting.entries = [
            {
                reference: 'provider.location.description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected async activate(model: IMemberProviderRelationshipsModelData): Promise<void> {
        if(!model) return;

        this.caseId = model.caseId ?? this.caseId;
        this.memberId = model.memberId ?? this.memberId;
        this.readonly = model.readonly ?? this.readonly;
        this.configurable = model.configurable ?? this.configurable;
    }

    protected bind(): void {
        this.checkPermissions();
        if(this.configurable) {
            this.loadCustomFieldTargets();
        }
        this.reload();
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting: sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'provider.location.description',
                        direction: SortDirection.Ascending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
        //as in member-pharmacy, prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    public async reload(): Promise<void> {
        await this.loadProviders();

        // only load member plans and relationships if grid is not readonly
        if(!this.readonly) {
            await this.loadMemberPlans();
            await this.loadMemberProviderRelationships();
        }
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
    }

    protected closeSaveMemberProviderModal(): void {
        if(this.$saveMemberProviderForm.reset) {
            this.$saveMemberProviderForm.reset();
        }
        this.$saveMemberProviderModal.close();
    }

    protected async handleSaveMemberProviderFormSuccess(): Promise<void> {
        if(this.$saveMemberProviderForm.reset) {
            this.$saveMemberProviderForm.reset();
        }
        this.$saveMemberProviderModal.close();
        await this.reload();
    }

    protected saveMemberProvider(memberProvider: CD.MemberProvider | null = null): void {
        this.editedMemberProvider = memberProvider ? deepClone(memberProvider) : {};
        this.$saveMemberProviderModal.open();
    }

    ////****************** Service Calls ********************************************////
    private async loadCustomFieldTargets(): Promise<void> {
        this.providerCustomFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CD.Provider');
        this.providerFacilityCustomFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CD.ProviderFacility');
        this.providerGroupCustomFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CD.ProviderGroup');
    }

    @apiCall
    private async loadProviders(): Promise<void> {
        if(!this.memberId) { return; }

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting,
            includeInactive: false
        };

        const includes = [
            'provider',
            'provider.location',
            'provider.location.facility',
            'provider.location.facility.group',
            'relationship',
            'member',
            'member.plan',
            'member.plan.products',
            'member.plan.healthPlan',
            'member.plan.lineOfBusiness',
            'member.primaryCarePhysician'
        ];

        const fields = [
            'dateRange.end',
            'dateRange.start',
            'member.plan.healthPlan.description',
            'member.plan.identifier.primary',
            'member.plan.lineOfBusiness.description',
            'member.plan.products.description',
            'member.primaryCarePhysician.id',
            'provider.custom.lookup.*',
            'provider.custom.text.*',
            'provider.description',
            'provider.identifier.nationalProvider',
            'provider.identifier.primary',
            'provider.location.description',
            'provider.location.facility.custom.lookup.*',
            'provider.location.facility.custom.text.*',
            'provider.location.facility.group.custom.lookup.*',
            'provider.location.facility.group.custom.text.*',
            'relationship.description',
            'relationship.flag.primaryCarePhysician'
        ];

        try {
            this.state.beginLoad();

            const response = await this.providerService.loadMemberProvidersByMember(this.memberId, options, includes, fields);
            this.providers = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadMemberPlans(): Promise<void> {
        if(!this.memberId) return;

        try {
            this.state.beginLoad();

            const includes = [
                'healthPlan'
            ];

            const fields = [
                'healthPlan.description'
            ];

            const response = await this.memberService.loadPlansByMember(this.memberId, undefined, includes, fields);
            this.memberPlans = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadMemberProviderRelationships(): Promise<void> {
        try {
            this.state.beginLoad();

            const fields = [
                'description'
            ];

            const response = await this.memberService.loadMemberProviderRelationships({}, [], fields);
            this.relationships = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async deactivateMemberProvider(memberProvider: CD.MemberProvider): Promise<void> {
        if(!memberProvider.id) return;

        const result = await confirmation.showDeactivate('cd:memberProviderRelationship.$', 1, false);
        if(result.dialogResponse === confirmation.options.CANCEL) { return; }

        if(!(result.dialogResponse === confirmation.options.OK)) {
            return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.memberService.activateMemberProvider(memberProvider.id, false);
            isSuccessful = response.isSuccessful;
            this.reload();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.activate('cd:memberProviderRelationship.$', false, isSuccessful);
            this.state.endSave();
        }
    }
}

export interface IMemberProviderRelationshipsModelData {
    caseId: string;
    memberId: string;
    readonly?: boolean;
    configurable?: boolean;
}

/**
 * Opens the member/provider relationships modal with the global dialog
 * @param caseId The caseId to use
 * @param memberId The memberId to use
 * @param readonly Readonly boolean
 * @param configurable configurable boolean
 */
 export function openMemberProviderRelationships(caseId: string, memberId: string, readonly: boolean = false, configurable: boolean = false): void {
    if(!caseId || !memberId) {
        throw new Error('caseId or memberId is missing or invalid');
    }

    const dialogOptions: IDialogOptions = {
        width: '10',
        height: '11',
        helpTopic: 'cm.member-provider-relationships',
        title: {
            key: 'cm:routes.memberProviderRelationships.$title'
        },
        print: true,
        popout: {
            route: `cm.member-provider-relationships`,
            params: {
                caseId: caseId
            }
        }
    };

    const modelData: IMemberProviderRelationshipsModelData = {
        caseId, memberId, readonly, configurable
    };

    const modalData: IModalData = {
        componentId: `app/cd/components/member/member-providers-grid`,
        modelData: modelData,
        dialogOptions: dialogOptions
    };

    openGlobalModal(modalData);
}
